import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { GAME_MODE_PRACTICE, getGameModeLabel, parseGameMode } from '../../utils/seasonType';

const GamesListPage: React.FC = () => {
  const [games, setGames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { seasonId } = useParams();

  const clientId = sessionManager.getClientId();
  const gamesMode = parseGameMode(sessionManager.getGamesMode());

  const handleTeamsClick = (gameId: string) => {
    sessionManager.setGameId(gameId);
    navigate(`/seasons/${seasonId}/teams`);
  };

  const handlePlayersClick = (gameId: string) => {
    sessionManager.setGameId(gameId);
    navigate(`/seasons/${seasonId}/players`);
  };

  const handleEditStatsClick = (gameId: string) => {
    sessionManager.setGameId(gameId);
    navigate(`/games/${gameId}/stats`);
  };

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`${API_URLS.GET_SEASON_GAMES}`, {
          params: { clientId, seasonId, gameMode: gamesMode },
        });

        if (response.data.success) {
          setGames(response.data.data);
        } else {
          setError(response.data.message || 'Failed to fetch games');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch games');
      } finally {
        setLoading(false);
      }
    };

    if (clientId && seasonId) {
      fetchGames();
    }
  }, [clientId, seasonId, gamesMode]);

  if (loading) {
    return (
      <div className="w-full mb-6 overflow-hidden">
        <div className="text-center py-8">Loading games...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full mb-6 overflow-hidden">
        <div className="text-center text-red-600 py-8">{error}</div>
      </div>
    );
  }

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#05396b]">
          Games Reports ({getGameModeLabel(gamesMode)})
        </h1>
        <p className="mt-2 text-sm sm:text-base text-[#05396b] font-medium max-w-3xl">
          Complete list of season games. Select a game to drill into detailed analytics, KPIs and player performance.
        </p>
      </header>

      <div className="bg-white border border-[#bccedf] rounded-lg">
        <div className="bg-[#05396b] text-white text-xl font-semibold px-4 py-2 rounded-t-lg">
          Games ({games.length})
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-[#f4fafd]">
                <th className="px-3 py-2 border-b text-left">{gamesMode === GAME_MODE_PRACTICE ? 'Game' : 'Opponent'}</th>
                <th className="px-3 py-2 border-b text-center">Date</th>
                <th className="px-3 py-2 border-b text-center">Type</th>
                <th className="px-3 py-2 border-b text-center">Outcome</th>
                <th className="px-3 py-2 border-b text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {games.map((game, index) => (
                <tr key={game._id} className={index % 2 === 0 ? 'bg-white' : 'bg-green-50'}>
                  <td
                    className="px-3 py-2 border-b font-medium max-w-[9rem] md:max-w-none truncate"
                    title={
                      gamesMode === GAME_MODE_PRACTICE
                        ? game.name || game.opponent?.name || game.opponentName || 'Intra Squad'
                        : game.opponent?.name || game.opponentName || 'TBD'
                    }
                  >
                    {gamesMode === GAME_MODE_PRACTICE
                      ? game.name || game.opponent?.name || game.opponentName || 'Intra Squad'
                      : game.opponent?.name || game.opponentName || 'TBD'}
                  </td>
                  <td className="px-3 py-2 border-b text-center">{game.date}</td>
                  <td className="px-3 py-2 border-b text-center">{game.type}</td>
                  <td className="px-3 py-2 border-b text-center">
                    <span
                      className={`px-2 py-1 rounded text-sm font-medium ${
                        game.outcome === 'Win' || game.outcome === 'W'
                          ? 'bg-green-100 text-green-800'
                          : game.outcome === 'Loss' || game.outcome === 'L'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {game.outcome || 'TBD'}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-b text-center">
                    <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 items-center justify-center">
                      <button
                        onClick={() => handleTeamsClick(game._id)}
                        className="bg-[#05396b] text-white px-2 py-1 rounded text-xs hover:bg-[#04325a] transition-colors min-w-[50px]"
                      >
                        Teams
                      </button>
                      <button
                        onClick={() => handlePlayersClick(game._id)}
                        className="bg-[#05396b] text-white px-2 py-1 rounded text-xs hover:bg-[#04325a] transition-colors min-w-[50px]"
                      >
                        Players
                      </button>
                      <button
                        onClick={() => handleEditStatsClick(game._id)}
                        className="bg-[#05396b] text-white px-2 py-1 rounded text-xs hover:bg-[#04325a] transition-colors min-w-[60px]"
                      >
                        Stats
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {games.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No {getGameModeLabel(gamesMode).toLowerCase()} games found for this season
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GamesListPage;
